import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Wallet, QrCode, Download, Calendar, MapPin, Ticket, ChevronLeft, ChevronRight, Share2, Smartphone } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/koo5s91n_BluevsBlue1%20%281%29.png";

export default function MyWalletPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [wallet, setWallet] = useState({ tickets: [] });
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [passData, setPassData] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => { fetchWallet(); }, []);

  const fetchWallet = async () => {
    try {
      const res = await axios.get(`${API}/user/wallet`, { withCredentials: true });
      setWallet(res.data || { tickets: [] });
      const focusTicket = searchParams.get("ticket");
      if (focusTicket && res.data?.tickets) {
        const idx = res.data.tickets.findIndex(t => t.ticket_id === focusTicket);
        if (idx >= 0) setCurrentIdx(idx);
      }
    } catch {}
    finally { setLoading(false); }
  };

  const loadPassData = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/user/wallet/pass/${ticketId}`, { withCredentials: true });
      setPassData(res.data);
    } catch { toast.error("Failed to load pass data"); }
  };

  useEffect(() => {
    if (wallet.tickets?.length > 0) {
      loadPassData(wallet.tickets[currentIdx]?.ticket_id);
    }
  }, [currentIdx, wallet]);

  const saveTicketAsImage = () => {
    if (!passData) return;
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 600;
    const ctx = canvas.getContext("2d");

    // Background
    ctx.fillStyle = "#0a1e3d";
    ctx.fillRect(0, 0, 400, 600);

    // Header
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 20px Arial";
    ctx.textAlign = "center";
    ctx.fillText("BLUE vs. BLUE", 200, 40);
    ctx.font = "12px Arial";
    ctx.fillStyle = "#7eb8da";
    ctx.fillText("Event Ticket", 200, 60);

    // Divider
    ctx.strokeStyle = "#1a3a5c";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(30, 80);
    ctx.lineTo(370, 80);
    ctx.stroke();

    // Event details
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 18px Arial";
    ctx.fillText(passData.event_title || "Event", 200, 115);
    ctx.font = "14px Arial";
    ctx.fillStyle = "#94a3b8";
    if (passData.event_date) {
      try { ctx.fillText(new Date(passData.event_date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }), 200, 140); } catch {}
    }
    if (passData.event_location) ctx.fillText(passData.event_location, 200, 160);

    // Ticket info
    ctx.fillStyle = "#7eb8da";
    ctx.font = "bold 14px Arial";
    ctx.fillText(passData.ticket_type || "General Admission", 200, 195);
    ctx.fillStyle = "#ffffff";
    ctx.font = "14px Arial";
    ctx.fillText(passData.attendee_name || "", 200, 220);

    // QR Code
    if (passData.qr_code) {
      const img = new Image();
      img.onload = () => {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(125, 250, 150, 150);
        ctx.drawImage(img, 130, 255, 140, 140);
        ctx.fillStyle = "#94a3b8";
        ctx.font = "11px monospace";
        ctx.fillText(passData.qr_data || "", 200, 420);
        
        // Status
        ctx.fillStyle = passData.status === "valid" ? "#22c55e" : "#94a3b8";
        ctx.font = "bold 16px Arial";
        ctx.fillText(passData.status?.toUpperCase() || "VALID", 200, 460);

        // Footer
        ctx.fillStyle = "#475569";
        ctx.font = "10px Arial";
        ctx.fillText("Present this QR code at the event entrance", 200, 510);
        ctx.fillText("bluevsblue.org", 200, 530);

        // Download
        const link = document.createElement("a");
        link.download = `ticket-${passData.ticket_id}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
        toast.success("Ticket saved to your device!");
      };
      img.src = `data:image/png;base64,${passData.qr_code}`;
    }
  };

  const shareTicket = async () => {
    if (!passData) return;
    const url = `${window.location.origin}/my-wallet?ticket=${passData.ticket_id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `Ticket: ${passData.event_title}`, text: `My ticket for ${passData.event_title}`, url });
      } catch {}
    } else {
      navigator.clipboard?.writeText(url);
      toast.success("Ticket link copied!");
    }
  };

  const ticket = wallet.tickets?.[currentIdx];

  return (
    <Layout>
      <section className="gradient-hero py-8">
        <div className="container-custom">
          <h1 className="text-heading-1 text-white mb-2 flex items-center gap-3"><Wallet className="w-8 h-8" /> My Wallet</h1>
          <p className="text-white/80">Your digital tickets for easy event check-in</p>
        </div>
      </section>

      <section className="section-spacing">
        <div className="container-custom max-w-lg mx-auto">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-deep-blue" /></div>
          ) : wallet.tickets?.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border">
              <Wallet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-600">No tickets in wallet</h3>
              <p className="text-slate-400 mt-1">Purchase event tickets to add them here</p>
            </div>
          ) : (
            <>
              {/* Navigation */}
              {wallet.tickets.length > 1 && (
                <div className="flex items-center justify-between mb-4">
                  <Button variant="ghost" size="sm" disabled={currentIdx <= 0} onClick={() => setCurrentIdx(p => p - 1)}><ChevronLeft className="w-5 h-5" /></Button>
                  <span className="text-sm text-slate-500">{currentIdx + 1} of {wallet.tickets.length}</span>
                  <Button variant="ghost" size="sm" disabled={currentIdx >= wallet.tickets.length - 1} onClick={() => setCurrentIdx(p => p + 1)}><ChevronRight className="w-5 h-5" /></Button>
                </div>
              )}

              {/* Ticket Card */}
              {ticket && passData && (
                <div className="bg-gradient-to-b from-[#0a1e3d] to-[#1a3a5c] rounded-2xl overflow-hidden shadow-2xl" data-testid="wallet-ticket-card">
                  {/* Header */}
                  <div className="p-6 text-center border-b border-white/10">
                    <img src={LOGO_URL} alt="Blue vs Blue" className="h-10 mx-auto mb-2 rounded" />
                    <span className="text-awareness-blue text-xs font-semibold uppercase tracking-wider">Event Ticket</span>
                  </div>

                  {/* Event Info */}
                  <div className="p-6 text-center">
                    <h2 className="text-white text-xl font-bold mb-2">{passData.event_title || "Event"}</h2>
                    {passData.event_date && (
                      <p className="text-white/60 text-sm flex items-center justify-center gap-1 mb-1">
                        <Calendar className="w-3 h-3" /> {new Date(passData.event_date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
                      </p>
                    )}
                    {passData.event_location && (
                      <p className="text-white/60 text-sm flex items-center justify-center gap-1">
                        <MapPin className="w-3 h-3" /> {passData.event_location}
                      </p>
                    )}
                  </div>

                  {/* Ticket Details */}
                  <div className="px-6 pb-2 grid grid-cols-2 gap-4 text-center">
                    <div><span className="text-white/40 text-[10px] uppercase">Type</span><p className="text-white text-sm font-semibold">{passData.ticket_type || "General"}</p></div>
                    <div><span className="text-white/40 text-[10px] uppercase">Attendee</span><p className="text-white text-sm font-semibold">{passData.attendee_name}</p></div>
                  </div>

                  {/* QR Code */}
                  <div className="p-6 flex flex-col items-center">
                    <div className="bg-white p-3 rounded-xl">
                      {ticket.qr_code ? (
                        <img src={`data:image/png;base64,${ticket.qr_code}`} alt="QR Code" className="w-44 h-44" />
                      ) : (
                        <QrCode className="w-44 h-44 text-slate-300" />
                      )}
                    </div>
                    <p className="text-white/40 text-xs mt-2 font-mono">{ticket.qr_data}</p>
                    <span className={`mt-3 px-4 py-1 rounded-full text-sm font-bold ${passData.status === 'valid' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}`}>
                      {passData.status?.toUpperCase()}
                    </span>
                  </div>

                  {/* Dashed separator */}
                  <div className="px-4"><div className="border-t border-dashed border-white/20" /></div>

                  {/* Actions */}
                  <div className="p-4 grid grid-cols-2 gap-2">
                    <Button onClick={saveTicketAsImage} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 text-sm" data-testid="save-ticket-btn">
                      <Download className="w-4 h-4 mr-1" /> Save to Phone
                    </Button>
                    <Button onClick={shareTicket} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 text-sm" data-testid="share-ticket-btn">
                      <Share2 className="w-4 h-4 mr-1" /> Share
                    </Button>
                  </div>

                  {/* Wallet buttons */}
                  <div className="px-4 pb-4 space-y-2">
                    <Button onClick={() => toast.info("Apple Wallet requires Apple Developer Certificate ($99/yr). Configure in Admin > Settings.")} className="w-full bg-black text-white hover:bg-gray-900 text-sm" data-testid="apple-wallet-btn">
                      <Smartphone className="w-4 h-4 mr-2" /> Add to Apple Wallet
                    </Button>
                    <Button onClick={() => toast.info("Google Wallet requires Google Cloud service account. Configure in Admin > Settings.")} className="w-full bg-white text-black hover:bg-gray-100 text-sm" data-testid="google-wallet-btn">
                      <Wallet className="w-4 h-4 mr-2" /> Add to Google Wallet
                    </Button>
                  </div>
                </div>
              )}

              <p className="text-center text-xs text-slate-400 mt-4">Present the QR code at the event entrance for check-in</p>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
